import { FormMetaData } from '../@types/form-props';
import { PanelData } from '../@types/panel-data';
import { StepData } from '../@types/step-data';
import Action from './action';
import AddFormDataAction from './action/add-form-data-action';
import GoToAction from './action/go-to-action';
import RedirectAction from './action/redirect-action';
import SubmitFormAction from './action/submit-form-action';
import getNextItem from './get-next-item';
import { isPanel } from './panel';

/** Returns an key-value map of NSP model keys to actions. */
const getActionMap = (): Record<string, typeof Action> => ({
  action_go_to: GoToAction,
  action_submit_form: SubmitFormAction,
  action_redirect: RedirectAction,
  action_form_option: AddFormDataAction,
});

/** Returns true if the given step can be safely typed as an Action. */
function isAction(step: StepData): step is Action {
  return Object.keys(getActionMap()).includes(step._modelApiKey);
}

/**
 * Performs any necessary action logic of following steps and returns the next panel to
 * display (or none if the final action is to redirect), plus the formData with any changes.
 * Actions may throw an error for example in the case of server-side validation.
 *
 * @returns {[PanelData, {}]} A tuple containing the new panel to move to and the new formData to set.
 * @throws Any exception which may block moving forward.
 */
export default async function moveForward(
  currentPanel: PanelData,
  steps: StepData[],
  formData: {},
  formMetaData?: FormMetaData
): Promise<[PanelData, {}]> {
  let nextStep: StepData = getNextItem(steps, currentPanel);

  // clone formData so we can mutate it safely within actions
  formData = { ...formData };

  // walk through steps until we either get to a panel or get nothing
  while (nextStep && !isPanel(nextStep)) {
    if (!isAction(nextStep)) {
      throw `Unexpected step ${nextStep._modelApiKey} (${nextStep.stepName})`;
    }

    const DynamicAction = getActionMap()[nextStep._modelApiKey];
    const action = new DynamicAction({ step: nextStep, steps, formData, formMetaData });
    nextStep = await action.perform();
  }

  return [nextStep as PanelData, formData];
}
